import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  TrendingDown,
  TrendingUp,
  Users,
  FileText,
  AlertTriangle,
  Info,
  ArrowLeft,
} from "lucide-react";
import ApiService from "@/services/api";
import {
  ValidationSession,
  ValidationIssue,
  GetValidationSessionResponse,
  GetValidationIssuesResponse,
  GetEPSReconciliationResponse,
  EPSReconciliation,
  IssuesByCategoryMap,
  EmployeeWithIssues,
  GetEmployeesWithIssuesResponse,
} from "@/types/payroll-validator";
import { IssueDetailCard } from "@/components/payroll-validator/IssueDetailCard";
import { EPSReconciliationCard } from "@/components/payroll-validator/EPSReconciliationCard";
import { StatutoryReportsCard } from "@/components/payroll-validator/StatutoryReportsCard";
import { LoadingState } from "@/components/payroll-validator/LoadingState";
import { CategoryIssueGroup } from "@/components/payroll-validator/CategoryIssueGroup";
import { EmployeeIssuesGrid } from "@/components/payroll-validator/EmployeeIssuesGrid";
import { ExportsCard } from "@/components/payroll-validator/ExportsCard";

export const PayrollValidatorResultsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<ValidationSession | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [employees, setEmployees] = useState<EmployeeWithIssues[]>([]);
  const [epsReconciliations, setEpsReconciliations] = useState<
    EPSReconciliation[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setError("No session ID provided");
      setLoading(false);
      return;
    }

    let pollInterval: NodeJS.Timeout;

    const pollSessionStatus = async () => {
      try {
        const sessionData = await ApiService.request<GetValidationSessionResponse>(
          "getValidationSession",
          {
            urlParams: { sessionId },
            skipAuth: true,
          },
        );
        setSession(sessionData);

        if (sessionData.status === "completed") {
          clearInterval(pollInterval);

          // Load issues
          const issuesResponse =
            await ApiService.request<GetValidationIssuesResponse>(
              "getValidationIssues",
              {
                urlParams: { sessionId },
                skipAuth: true,
              },
            );
          setIssues(issuesResponse.issues);

          // Load employees with issue counts
          try {
            const employeesResponse =
              await ApiService.request<GetEmployeesWithIssuesResponse>(
                "getEmployeesWithIssues",
                {
                  urlParams: { sessionId },
                  skipAuth: true,
                },
              );
            setEmployees(employeesResponse.employees || []);
          } catch (employeeError) {
            console.error("Failed to load employees:", employeeError);
            setEmployees([]);
          }

          // Try to load EPS reconciliations (optional)
          try {
            const epsResponse =
              await ApiService.request<GetEPSReconciliationResponse>(
                "getEPSReconciliation",
                {
                  urlParams: { sessionId },
                  skipAuth: true,
                },
              );
            setEpsReconciliations(epsResponse.reconciliations || []);
          } catch (epsError) {
            // EPS is optional, don't fail if not present
            console.log("No EPS reconciliation data available");
          }

          setLoading(false);
        } else if (sessionData.status === "failed") {
          setError("Validation failed. Please try again.");
          setLoading(false);
          clearInterval(pollInterval);
        }

        setPollCount((prev) => prev + 1);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to fetch validation status",
        );
        setLoading(false);
        clearInterval(pollInterval);
      }
    };

    // Initial poll
    pollSessionStatus();

    // Set up polling interval
    pollInterval = setInterval(pollSessionStatus, 2000);

    // Cleanup
    return () => clearInterval(pollInterval);
  }, [sessionId]);

  const groupIssuesByCategory = (
    issues: ValidationIssue[],
  ): IssuesByCategoryMap => {
    return issues.reduce((acc, issue) => {
      if (!acc[issue.category]) {
        acc[issue.category] = [];
      }
      acc[issue.category].push(issue);
      return acc;
    }, {} as IssuesByCategoryMap);
  };

  const getCategoryInfo = (category: string) => {
    const categoryMap: Record<
      string,
      { title: string; icon: React.ReactNode; color: string }
    > = {
      pension: {
        title: "Pension Compliance",
        icon: <TrendingUp className="h-5 w-5" />,
        color: "text-blue-600 bg-blue-50 border-blue-200",
      },
      tax: {
        title: "Tax Code Issues",
        icon: <FileText className="h-5 w-5" />,
        color: "text-green-600 bg-green-50 border-green-200",
      },
      ni: {
        title: "National Insurance",
        icon: <Users className="h-5 w-5" />,
        color: "text-purple-600 bg-purple-50 border-purple-200",
      },
      pay_id: {
        title: "HMRC Pay ID / RTI",
        icon: <AlertCircle className="h-5 w-5" />,
        color: "text-orange-600 bg-orange-50 border-orange-200",
      },
      pay_history: {
        title: "Pay History",
        icon: <TrendingDown className="h-5 w-5" />,
        color: "text-red-600 bg-red-50 border-red-200",
      },
      employee_info: {
        title: "Employee Information",
        icon: <Users className="h-5 w-5" />,
        color: "text-indigo-600 bg-indigo-50 border-indigo-200",
      },
      student_loan: {
        title: "Student Loan",
        icon: <FileText className="h-5 w-5" />,
        color: "text-yellow-600 bg-yellow-50 border-yellow-200",
      },
    };

    return (
      categoryMap[category] || {
        title: category,
        icon: <Info className="h-5 w-5" />,
        color: "text-gray-600 bg-gray-50 border-gray-200",
      }
    );
  };

  if (error) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/payroll-validator")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Upload
        </Button>
      </div>
    );
  }

  if (loading) {
    return <LoadingState session={session} pollCount={pollCount} />;
  }

  if (!session) {
    return null;
  }

  const issuesByCategory = groupIssuesByCategory(issues);
  const categoryKeys = Object.keys(issuesByCategory).sort((a, b) => {
    // Sort by severity: categories with critical issues first
    const aCritical = issuesByCategory[a].filter(
      (i) => i.severity === "critical",
    ).length;
    const bCritical = issuesByCategory[b].filter(
      (i) => i.severity === "critical",
    ).length;
    return bCritical - aCritical;
  });

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/payroll-validator")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Upload
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Validation Results
        </h1>
        <p className="text-lg text-gray-600">
          Analysis completed for {session.total_employees} employee
          {session.total_employees !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Total Issues */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Total Issues
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {session.total_issues}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Critical Issues */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Critical Issues
                </p>
                <p className="text-3xl font-bold text-red-600">
                  {session.critical_issues}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Warnings */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Warnings
                </p>
                <p className="text-3xl font-bold text-orange-600">
                  {session.warning_issues}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Info className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Impact Alert */}
      {session.estimated_cost_impact && session.estimated_cost_impact > 0 && (
        <Alert className="mb-8 bg-yellow-50 border-yellow-300">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <AlertTitle className="text-yellow-900 text-lg font-semibold">
            Potential Cost Impact
          </AlertTitle>
          <AlertDescription className="text-yellow-800 text-base">
            These issues could cost you approximately{" "}
            <strong className="text-xl">
              £
              {session.estimated_cost_impact.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>{" "}
            if not resolved before migrating payroll. This includes potential
            penalties, arrears, and administrative costs.
          </AlertDescription>
        </Alert>
      )}

      {/* Success Message (No Issues) */}
      {session.total_issues === 0 && (
        <Alert className="mb-8 bg-green-50 border-green-200">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <AlertTitle className="text-green-900 text-lg font-semibold">
            No Issues Found!
          </AlertTitle>
          <AlertDescription className="text-green-800">
            Your payroll data looks great! We ran {pollCount * 10} validation
            checks and didn't find any critical issues or warnings.
          </AlertDescription>
        </Alert>
      )}

      {/* Employee Issues Grid */}
      {employees.length > 0 && sessionId && (
        <div className="mb-8">
          <EmployeeIssuesGrid employees={employees} sessionId={sessionId} />
        </div>
      )}

      {/* Exports */}
      {sessionId && (
        <div className="mb-8">
          <ExportsCard sessionId={sessionId} />
        </div>
      )}

      {/* EPS Reconciliation */}
      {epsReconciliations.length > 0 && (
        <div className="space-y-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            EPS Reconciliation
          </h2>
          {epsReconciliations.map((reconciliation) => (
            <EPSReconciliationCard
              key={reconciliation.reconciliation_id}
              reconciliation={reconciliation}
            />
          ))}
        </div>
      )}

      {/* Statutory Reports */}
      {sessionId && (
        <div className="mb-8">
          <StatutoryReportsCard sessionId={sessionId} />
        </div>
      )}

      {/* Next Steps */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
          <CardDescription>What to do with these results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Review Critical Issues First
                </h3>
                <p className="text-sm text-gray-600">
                  Focus on issues marked as "Critical" - these need to be
                  resolved before payroll migration.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Contact Your Current Provider
                </h3>
                <p className="text-sm text-gray-600">
                  Use the "How to Fix" guidance in each issue to know exactly
                  what information to request from your current payroll
                  provider.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-purple-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Re-run Validation
                </h3>
                <p className="text-sm text-gray-600">
                  Once you have corrected data, run the validator again to
                  ensure all issues are resolved.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <Button onClick={() => navigate("/payroll-validator")} size="lg">
              Upload New Files
            </Button>
            <Button variant="outline" size="lg" onClick={() => window.print()}>
              Export Report
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
